import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ConnectionState, type AppConfig, type Toast, type ServerConfig } from './types';
import { DEFAULT_CONFIG, TOAST_DURATION_MS, RECONNECT_STRATEGY, STATUS_BAR_AUTO_HIDE_DELAY_MS } from './constants';
import { X } from 'lucide-react';

// Components
import VideoPlayer from './components/VideoPlayer';
import StatusBar from './components/StatusBar';
import StatsPanel from './components/StatsPanel';
import DiagnosticDashboard from './components/DiagnosticDashboard';
import SettingsPanel from './components/SettingsPanel';
import ConnectionOverlay from './components/ConnectionOverlay';
import IceRacingPanel from './components/IceRacingPanel';
import { iceRacingService } from './core/IceRacingService';
import ToastContainer from './components/ToastContainer';
import DegradeCountdown from './components/DegradeCountdown';
import QuickActions from './components/QuickActions';
import FileTransferPanel from './components/FileTransferPanel';
import ClipboardFilesNotification from './components/ClipboardFilesNotification';
import FileManager from './components/FileManager/FileManager';
import P2PAssistPanel from './components/P2PAssistPanel';
import CrashNotification from './components/CrashNotification';
import P2PAssistDialog from './components/P2PAssistDialog';
import UnauthorizedScreen from './components/UnauthorizedScreen';
import type { FileInfo } from './core/FileTransferService';

// Services
import { webRTCService } from './core/WebRTCService';
import { inputService } from './core/InputService';
import { statsService } from './core/StatsService';
import { eventBus } from './core/EventBus';
import { fileTransferService } from './core/FileTransferService';
import { fileDownloadService } from './core/FileDownloadService';
import { clipboardQueryService } from './core/ClipboardQueryService';
import { p2pAssistService } from './core/P2PAssistService';
import { signalingService, type PeerInfo } from './core/SignalingService';
import { authService } from './core/AuthService';
import { apiClient } from './core/ApiClient';
import './components/FileManager/services/FileSearchStatusService'; // Initialize global status listener
import { useFileManagerStore } from './components/FileManager/store/useFileManagerStore';
import { WebRTCSystem } from './components/FileManager/api/WebRTCSystem';
import UploadManager from './components/FileManager/components/UI/UploadManager';
import ConflictModal from './components/FileManager/components/UI/ConflictModal';
import GlobalSearchPanel from './components/FileManager/components/UI/GlobalSearchPanel';
import SearchEngineInitModal from './components/FileManager/components/UI/SearchEngineInitModal';

const App: React.FC = () => {
  // 启动时一次性清理废弃的会话配置缓存（仅保留 statusBar/bladePosition）
  // 若旧版本写过这些 key，升级后第一次加载即清空，避免读取到残留值
  useEffect(() => {
    const DEPRECATED_KEYS = [
      'webrtcgo_videoCodec',
      'webrtcgo_servers',
      'webrtcgo_last_server',
      'webrtcgo_connection_mode',
      'webrtcgo_relay_server',
      'webrtcgo_relay_peer_id',
      'webrtcgo_relay_history',
      'p2pAssistDebugMode',
      'p2p_switch_diag',
    ];
    try {
      DEPRECATED_KEYS.forEach(k => localStorage.removeItem(k));
    } catch {}
  }, []);

  // Configuration
  const [config, setConfig] = useState<AppConfig>(() => {
    const base = { ...DEFAULT_CONFIG };
    try {
      const vis = localStorage.getItem('webrtcgo_statusBarVisibility');
      if (vis === 'auto' || vis === 'on' || vis === 'off') base.statusBarVisibility = vis;
      const ori = localStorage.getItem('webrtcgo_statusBarOrientation');
      if (ori === 'vertical' || ori === 'horizontal') base.statusBarOrientation = ori;
    } catch {}
    return base;
  });

  // Persist statusBar settings to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('webrtcgo_statusBarVisibility', config.statusBarVisibility);
      localStorage.setItem('webrtcgo_statusBarOrientation', config.statusBarOrientation);
    } catch {}
  }, [config.statusBarVisibility, config.statusBarOrientation]);

  // host 推送的截图器状态（screenCapturer 段）；尚未收到则为 null
  // 状态在 App 层维护是因为 host 在 DataChannel open 时只广播一次，
  // 若放在 SettingsPanel 内订阅则用户后开面板会错过那次广播。
  const [screenCapturer, setScreenCapturer] = useState<{
    active: 'auto' | 'wgc' | 'dxgi' | 'gdi';
    preferred: 'auto' | 'wgc' | 'dxgi' | 'gdi';
    userOverride: boolean;
    supportedOnThisOs: ('auto' | 'wgc' | 'dxgi' | 'gdi')[];
    lastBenchmark: null | {
      os: string;
      displaySignature: string;
      wgc: { tested: boolean; available: boolean; successCount: number; totalCount: number; avgMs: number; frameWidth: number; frameHeight: number; note: string };
      dxgi: { tested: boolean; available: boolean; successCount: number; totalCount: number; avgMs: number; frameWidth: number; frameHeight: number; note: string };
      gdi: { tested: boolean; available: boolean; successCount: number; totalCount: number; avgMs: number; frameWidth: number; frameHeight: number; note: string };
      winner: 'auto' | 'wgc' | 'dxgi' | 'gdi';
      ts: number;
    };
  } | null>(null);

  // Check URL params for file manager mode
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('mode') === 'file-manager') {
      setShowFileManager(true);
    }
  }, []);

  // 只读模式：默认只读（不带参数也是只读）。仅当显式 ?viewonly=false 时才以"可控"作为初值。
  // 该值仅决定连接完成后开关的初始状态；运行时通过状态栏开关可随时切换。
  const initialViewOnly = useMemo(
    () => new URLSearchParams(window.location.search).get('viewonly') !== 'false',
    []
  );
  const [viewOnlyMode, setViewOnlyMode] = useState<boolean>(initialViewOnly);
  const toggleViewOnlyMode = useCallback(() => {
    setViewOnlyMode(prev => !prev);
  }, []);

  // 授权状态：默认 true，AuthService init 完成前先放行避免页面闪烁；
  // emit auth:unauthorized 时置 false → 整个主界面被 UnauthorizedScreen 覆盖
  const [isAuthorized, setIsAuthorized] = useState<boolean>(true);

  // noauth 免鉴权模式（由 URL ?noauth=true&noauthPwd= 决定，AuthService.init 同步判定）
  // 为 true 时隐藏依赖 token 的功能入口（解锁/锁定/分享远程）
  const [isNoauthMode, setIsNoauthMode] = useState<boolean>(false);

  // Connection state
  const [connectionState, setConnectionState] = useState<ConnectionState>(ConnectionState.DISCONNECTED);
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const [logs, setLogs] = useState<Array<{ id: string; timestamp: Date; level: 'info' | 'warn' | 'error' | 'success'; message: string }>>([]);

  // UI state
  const [showStats, setShowStats] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);  // Stage 2C: 完整诊断 Dashboard
  const [showIceDetail, setShowIceDetail] = useState(false);  // ICE 详情回看面板（从引擎诊断报告打开）
  const [showSettings, setShowSettings] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [showFileManager, setShowFileManager] = useState(false);
  const [showP2PAssistPanel, setShowP2PAssistPanel] = useState(false);
  const [isP2PAssistRunning, setIsP2PAssistRunning] = useState(false);
  const [hasBestP2PChannel, setHasBestP2PChannel] = useState(false);
  const [isUiVisible, setIsUiVisible] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);

  // Crash notification state
  const [crashInfo, setCrashInfo] = useState<string | null>(null);

  // Host waiting state (legacy: waiting for host to come online with countdown).
  // 统一退避重连后此状态不再被设置，但保留兜底以避免无关回归。
  const [hostWaitingInfo, setHostWaitingInfo] = useState<{ attempt: number; maxAttempts: number } | null>(null);

  // 信令重连提示（B 选项）：阈值后才在 Overlay 显示柔和的"信令重连中（已尝试 N 次）"副文案。
  // 重连成功（signaling:open）或新一轮重连开始（attempt=1）时清空。
  const [signalingRetryAttempt, setSignalingRetryAttempt] = useState<number | null>(null);

  // MSTSC minimized state (remote desktop capture access denied)
  const [captureAccessDenied, setCaptureAccessDenied] = useState(false);

  // Connected peers state (for status bar display)
  const [connectedPeers, setConnectedPeers] = useState<PeerInfo[]>([]);

  // Input state - 合并为单一开关
  const [inputEnabled, setInputEnabled] = useState(true);
  const [remoteCursor, setRemoteCursor] = useState('default');
  const [remoteCursorVisible, setRemoteCursorVisible] = useState(false);  // 默认隐藏，等远程发送 visible: true 才显示
  const [isPointerLocked, setIsPointerLocked] = useState(false);

  // Canvas zoom/pan state
  const [canvasZoom, setCanvasZoom] = useState(1);
  const [canvasPanX, setCanvasPanX] = useState(0);
  const [canvasPanY, setCanvasPanY] = useState(0);
  const [isPanMode, setIsPanMode] = useState(false);

  // Status bar auto-hide state
  const [statusBarAutoHidden, setStatusBarAutoHidden] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);
  // FileManager 文件上传/排队中的任务数（驱动 StatusBar 上传按钮状态显示）
  const fmUploadCount = useFileManagerStore(
    s => s.uploadTasks.filter(t => t.status === 'uploading' || t.status === 'pending').length
  );
  // 全局搜索 / 搜索引擎初始化弹窗状态（驱动顶层挂载 + 远程输入暂停）
  const isGlobalSearchOpen = useFileManagerStore(s => s.isGlobalSearchOpen);
  const isSearchEngineInitModalOpen = useFileManagerStore(s => s.isSearchEngineInitModalOpen);
  const isSearchEngineReinit = useFileManagerStore(s => s.isSearchEngineReinit);
  const closeSearchEngineInitModal = useFileManagerStore(s => s.closeSearchEngineInitModal);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const statusBarTimerRef = useRef<number | null>(null);
  const connectedTimeRef = useRef<number>(0);

  // Connection name for title
  const [connectionName, setConnectionName] = useState<string | null>(null);
  // 状态栏网吧信息：网吧名称 / 分组 / 终端座位号(seat) / 网吧备注(name) / IP
  const [venueName, setVenueName] = useState<string>('');
  const [venueGroup, setVenueGroup] = useState<string>('');
  const [terminalSeat, setTerminalSeat] = useState<string>('');
  const [terminalName, setTerminalName] = useState<string>('');
  const [terminalIp, setTerminalIp] = useState<string>('');

  // File transfer state
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);
  const [fileTransferProgress, setFileTransferProgress] = useState<{
    id: string;
    fileName: string;
    totalFiles: number;
    totalSize: number;
    transferredBytes: number;
    status: string;
    startTime: number;
    files: FileInfo[];
  } | null>(null);
  const [showFileTransferPanel, setShowFileTransferPanel] = useState(true);

  // Stats state
  const [stats, setStats] = useState({
    fps: 0,
    rtt: 0,
    bitrate: 0,
    packetLoss: 0,
    jitter: 0,
    resolution: '0x0',
    hostGpu: 'Unknown',
    encoderName: 'Unknown',
    captureMode: 'Unknown',
    videoCodec: 'H264',
    latencyBreakdown: { capture: 0, convert: 0, encode: 0, network: 0, jitterBuffer: 0, decode: 0, total: 0 },
    framesDecoded: 0,
    framesDropped: 0,
    keyframesDecoded: 0,
    transportType: 'unknown' as const,
    localCandidateType: 'unknown',
    remoteCandidateType: 'unknown',
    remoteAddress: '',
    hostPlatform: '',
  });
  const [chartData, setChartData] = useState<Array<{ time: number; bitrate: number; fps: number; packetLoss: number; jitterBuffer: number; rtt: number }>>([]);
  const [encoderBitrate, setEncoderBitrate] = useState(0);  // HOST 编码器目标码率 (bps)

  // Refs
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const directoryInputRef = useRef<HTMLInputElement | null>(null);
  const systemAudioRef = useRef<HTMLAudioElement | null>(null);
  const micAudioRef = useRef<HTMLAudioElement | null>(null);
  const configRef = useRef<AppConfig>(config); // Keep latest config for event handlers

  // Keep configRef in sync with latest config
  useEffect(() => {
    configRef.current = config;
  }, [config]);

  // Send initial audio config when DataChannel becomes ready
  useEffect(() => {
    const unsubDataChannelReady = eventBus.on('webrtc:dataChannelReady', (ready: boolean) => {
      if (ready) {
        // Send initial audio config based on current settings
        const currentConfig = configRef.current;
        webRTCService.sendAudioConfig(currentConfig.systemAudioEnabled, currentConfig.micAudioEnabled);
      }
    });

    return () => {
      unsubDataChannelReady();
    };
  }, []);

  // Add log entry
  const addLog = useCallback((message: string, level: 'info' | 'warn' | 'error' | 'success' = 'info') => {
    setLogs(prev => [...prev.slice(-49), {
      id: Math.random().toString(36).substr(2, 9),
      timestamp: new Date(),
      level,
      message,
    }]);
  }, []);

  // Add toast notification
  const addToast = useCallback((message: string, type: Toast['type'] = 'info') => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, TOAST_DURATION_MS);
  }, []);

  // 剪贴板上传：探测远程前台资源管理器/桌面目录，复用 FileManager 上传逻辑直接写入该目录。
  // 探测失败（非资源管理器/桌面、虚拟位置）则弹"上传失败"，不再走虚拟剪贴板。
  const uploadFilesToForegroundDir = useCallback(async (files: File[]) => {
    if (files.length === 0) {
      addToast('大哥，你选择的是一个空目录，里面啥都没有', 'warning');
      return;
    }
    const fmStore = useFileManagerStore.getState();
    const fsSys = fmStore.fs as WebRTCSystem;

    // 确保 fs-rpc 通道就绪（懒创建，不依赖文件管理器面板是否打开）
    if (!fsSys.isConnected) {
      const ch = await webRTCService.ensureFsRpcChannel();
      if (!ch) {
        addToast('上传失败：文件通道未就绪，请稍后重试', 'error');
        addLog('上传失败：fs-rpc 通道未就绪', 'error');
        return;
      }
      fsSys.setDataChannel(ch);
    }

    // 探测远程前台目录
    const probe = await fsSys.getForegroundExplorerPath();
    if (!probe.ok || !probe.path) {
      addToast('上传失败：请在远程打开资源管理器目录或桌面后重试', 'error');
      addLog('上传失败：远程前台不是资源管理器/桌面目录', 'error');
      return;
    }
    const targetDir = probe.path;

    const items = files.map(f => ({
      file: f,
      relativePath: (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name,
      isDirectory: false,
    }));
    const totalSize = files.reduce((sum, f) => sum + f.size, 0);
    const sizeMB = (totalSize / 1024 / 1024).toFixed(1);
    addToast(`正在上传 ${files.length} 个文件 (${sizeMB}MB) 到 ${targetDir}`, 'info');
    await fmStore.uploadFiles(items, targetDir);
    addLog(`已开始上传 ${files.length} 个文件到 ${targetDir}`, 'success');
  }, [addToast, addLog]);


  // Current server reference
  const currentServerRef = useRef<ServerConfig | null>(null);
  const [serverBaseUrl, setServerBaseUrl] = useState('');

  // Connection handlers
  const connect = useCallback(async (server: ServerConfig) => {

    // 连接时回滚 ICE 策略和 TURN 协议到自动（避免错误设置导致无法连接）
    if (config.icePolicy !== 'auto' || config.turnProtocol !== 'auto') {
      config.icePolicy = 'auto';
      config.turnProtocol = 'auto';
      setConfig(prev => ({ ...prev, icePolicy: 'auto', turnProtocol: 'auto' }));
    }

    // Save connection name for title
    setConnectionName(server.name);

    currentServerRef.current = server;

    // Try to get video element if not set
    if (!videoRef.current) {
      const video = document.querySelector('video') as HTMLVideoElement;
      if (video) {
        videoRef.current = video;
      }
    }

    if (!videoRef.current) {
      console.error('[App] No video element found!');
      addLog('视频元素未就绪，请稍后重试', 'error');
      addToast('视频元素未就绪', 'error');
      return;
    }

    addLog(`正在连接到 ${server.name} (${server.wsUrl})...`, 'info');

    // Initialize services with server URL
    webRTCService.initialize(config, videoRef.current, server.wsUrl);
    inputService.initialize(videoRef.current);

    try {
      await webRTCService.connect(server.wsUrl);
    } catch (e) {
      console.error('[App] Connection error:', e);
      addLog(`连接失败: ${(e as Error).message}`, 'error');
      addToast(`连接失败: ${(e as Error).message}`, 'error');
    }
  }, [config, addLog, addToast]);

  const disconnect = useCallback(() => {
    addLog('正在断开连接...', 'info');
    webRTCService.disconnect();
    statsService.stop();
    setServerBaseUrl('');
    setConnectionName(null); // Clear connection name
    setIsUiVisible(true);
    // 断开时回滚 ICE 策略和 TURN 协议到自动（避免仅P2P/仅TURN持续失败无法恢复）
    setConfig(prev => (prev.icePolicy !== 'auto' || prev.turnProtocol !== 'auto')
      ? { ...prev, icePolicy: 'auto', turnProtocol: 'auto' } : prev);
    addToast('已断开连接', 'info');
  }, [addLog, addToast]);

  // === Auth: 启动 token 刷新机制 + 401 兜底失败处理 ===
  // 注意：监听器必须在 authService.init() 之前注册。init 是 async 但 URL 无 token 的
  // 同步分支会立即 emit auth:unauthorized，如果监听器晚于 init 注册，事件会被丢掉。
  useEffect(() => {
    // help 纯说明页：跳过鉴权，避免 auth:unauthorized 清掉 URL 并覆盖成「访问受限」
    if (window.location.search === '?help') return;
    const unsub = eventBus.on('auth:unauthorized', () => {
      setIsAuthorized(false);                      // 主界面切到拒绝页
      addToast('登录信息失效，请重新登录', 'error');
      disconnect();
      // 清理 URL 全部参数（包括残留的 access_token 之外的查询参数）
      try {
        window.history.replaceState(null, '', window.location.pathname);
      } catch { /* ignore */ }
    });
    authService.init();
    // noauth 判定在 init 同步段完成（无网络），调用后即可读取
    setIsNoauthMode(authService.isNoauth());
    return () => {
      authService.dispose();
      unsub();
    };
  }, [addToast, disconnect]);

  // Connect via relay server
  const connectRelay = useCallback(async (relayConfig: { serverUrl: string; peerId: string }) => {

    // 连接时回滚 ICE 策略和 TURN 协议到自动（避免错误设置导致无法连接）
    if (config.icePolicy !== 'auto' || config.turnProtocol !== 'auto') {
      config.icePolicy = 'auto';
      config.turnProtocol = 'auto';
      setConfig(prev => ({ ...prev, icePolicy: 'auto', turnProtocol: 'auto' }));
    }

    // Save connection name for title (use peerId as name)
    setConnectionName(relayConfig.peerId);

    // Try to get video element if not set
    if (!videoRef.current) {
      const video = document.querySelector('video') as HTMLVideoElement;
      if (video) {
        videoRef.current = video;
      }
    }

    if (!videoRef.current) {
      console.error('[App] No video element found!');
      addLog('视频元素未就绪，请稍后重试', 'error');
      addToast('视频元素未就绪', 'error');
      return;
    }

    addLog(`正在通过中转服务器连接 (PeerID: ${relayConfig.peerId})...`, 'info');

    // Set currentServerRef for stats (use relay server URL)
    currentServerRef.current = {
      name: `Relay: ${relayConfig.peerId}`,
      wsUrl: relayConfig.serverUrl,
      httpUrl: '',
    };

    // Initialize services (no wsUrl for relay mode)
    webRTCService.initialize(config, videoRef.current);
    inputService.initialize(videoRef.current);

    try {
      await webRTCService.connectRelay(relayConfig);
    } catch (e) {
      console.error('[App] Relay connection error:', e);
      // 信令仍在自动重连周期中：静默 info 日志，不弹红条 toast。
      if (signalingService.waitingForHost) {
        addLog(`信令连接失败，正在自动重连: ${(e as Error).message}`, 'info');
        return;
      }
      addLog(`中转连接失败: ${(e as Error).message}`, 'error');
      addToast(`中转连接失败: ${(e as Error).message}`, 'error');
    }
  }, [config, addLog, addToast]);

  // Handle quick actions
  const handleQuickAction = useCallback((action: string) => {
    if (viewOnlyMode) return;
    switch (action) {
      case 'ctrl+alt+del':
      case 'unlock':
      case 'alt+tab':
      case 'win+d':
      case 'win+l':
      case 'ctrl+shift+esc':
      case 'win+tab':
      case 'alt+f4':
      case 'f11':
      case 'win+e':
      case 'win+r':
      case 'win+s':
      case 'win+i':
      case 'win+v':
      case 'win+p':
      case 'win+shift+s':
        inputService.sendSpecialKey(action);
        addToast(`已发送 ${action.toUpperCase()}`, 'success');
        break;
      case 'refresh':
        // Request keyframe
        addToast('正在刷新画面...', 'info');
        break;
    }
    setShowQuickActions(false);
  }, [addToast, viewOnlyMode]);

  // File drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    // 只读模式：完全不接受拖拽
    if (viewOnlyMode) return;
    // If FileManager is open, let it handle drag events
    if (showFileManager) return;

    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  }, [showFileManager, viewOnlyMode]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // Recursively get all files from directory entries
  const getAllFiles = async (items: DataTransferItemList): Promise<File[]> => {
    const files: File[] = [];

    // IMPORTANT: Collect all entries SYNCHRONOUSLY first!
    // DataTransferItemList items become invalid after any async operation
    const rootEntries: FileSystemEntry[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        const entry = item.webkitGetAsEntry();
        if (entry) {
          rootEntries.push(entry);
        }
      }
    }

    // Read all entries from a directory (readEntries may not return all at once)
    const readAllEntries = async (reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> => {
      const allEntries: FileSystemEntry[] = [];
      let entries: FileSystemEntry[];
      do {
        entries = await new Promise<FileSystemEntry[]>((resolve, reject) => {
          reader.readEntries(resolve, reject);
        });
        allEntries.push(...entries);
      } while (entries.length > 0);
      return allEntries;
    };

    const readEntry = async (entry: FileSystemEntry, path: string): Promise<void> => {
      if (entry.isFile) {
        const fileEntry = entry as FileSystemFileEntry;
        const file = await new Promise<File>((resolve, reject) => {
          fileEntry.file(
            (f) => {
              // Create new file with relative path
              const fileWithPath = new File([f], f.name, { type: f.type });
              Object.defineProperty(fileWithPath, 'webkitRelativePath', {
                value: path + f.name,
                writable: false,
              });
              resolve(fileWithPath);
            },
            reject
          );
        });
        files.push(file);
      } else if (entry.isDirectory) {
        const dirEntry = entry as FileSystemDirectoryEntry;
        const reader = dirEntry.createReader();
        // Must call readEntries repeatedly until it returns empty array
        const entries = await readAllEntries(reader);
        for (const childEntry of entries) {
          await readEntry(childEntry, path + entry.name + '/');
        }
      }
    };

    // Now process entries asynchronously (they're already captured)
    for (const entry of rootEntries) {
      await readEntry(entry, '');
    }

    return files;
  };

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    // 只读模式：拒绝拖拽上传
    if (viewOnlyMode) {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragging(false);
      return;
    }
    // If FileManager is open, let it handle drop events
    if (showFileManager) return;

    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);

    if (connectionState !== ConnectionState.CONNECTED) {
      addToast('请先连接到远程桌面', 'warning');
      return;
    }

    try {
      // Use webkitGetAsEntry to handle directories
      const items = e.dataTransfer.items;
      let files: File[];

      if (items && items.length > 0 && items[0].webkitGetAsEntry) {
        addToast('正在扫描文件...', 'info');
        files = await getAllFiles(items);
      } else {
        // Fallback to regular file list
        files = Array.from(e.dataTransfer.files);
      }

      if (files.length === 0) {
        addToast('大哥，你选择的是一个空目录，里面啥都没有', 'warning');
        return;
      }

      await uploadFilesToForegroundDir(files);
    } catch (err) {
      const message = err instanceof Error ? err.message : '未知错误';
      addToast(`文件传输失败: ${message}`, 'error');
      addLog(`文件传输失败: ${message}`, 'error');
    }
  }, [connectionState, addToast, addLog, showFileManager, viewOnlyMode]);

  // Toggle input (combined mouse + keyboard)
  const toggleInput = useCallback(() => {
    if (viewOnlyMode) return;
    setInputEnabled(prev => {
      const newState = !prev;
      inputService.setEnabled({ mouse: newState, keyboard: newState });
      return newState;
    });
  }, [viewOnlyMode]);

  // Canvas zoom/pan actions
  const ZOOM_STEP = 0.25;
  const MAX_ZOOM = 5;
  // 追踪鼠标位置用于 zoom-at-point
  const mousePosRef = useRef({ x: 0, y: 0 });
  useEffect(() => {
    const track = (e: MouseEvent) => { mousePosRef.current = { x: e.clientX, y: e.clientY }; };
    window.addEventListener('mousemove', track);
    return () => window.removeEventListener('mousemove', track);
  }, []);

  const handleZoomIn = useCallback(() => {
    setCanvasZoom(prev => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  }, []);
  const handleZoomOut = useCallback(() => {
    setCanvasZoom(prev => {
      const next = Math.max(prev - ZOOM_STEP, 1);
      if (next <= 1) {
        setCanvasPanX(0);
        setCanvasPanY(0);
        setIsPanMode(false);
      }
      return next;
    });
  }, []);
  // 以鼠标位置为中心缩放（Alt+/- 快捷键用）
  const handleZoomAtPoint = useCallback((zoomIn: boolean) => {
    const mx = mousePosRef.current.x - window.innerWidth / 2;
    const my = mousePosRef.current.y - window.innerHeight / 2;
    setCanvasZoom(prevZoom => {
      const newZoom = zoomIn
        ? Math.min(prevZoom + ZOOM_STEP, MAX_ZOOM)
        : Math.max(prevZoom - ZOOM_STEP, 1);
      if (newZoom <= 1) {
        setCanvasPanX(0);
        setCanvasPanY(0);
        setIsPanMode(false);
        return 1;
      }
      // 保持鼠标下的内容点不动: newPan = mouse - (mouse - oldPan) * newZoom / oldZoom
      const ratio = newZoom / (prevZoom || 1);
      setCanvasPanX(prev => mx - (mx - prev) * ratio);
      setCanvasPanY(prev => my - (my - prev) * ratio);
      return newZoom;
    });
  }, []);
  const handleResetZoom = useCallback(() => {
    setCanvasZoom(1);
    setCanvasPanX(0);
    setCanvasPanY(0);
    setIsPanMode(false);
  }, []);
  const handleTogglePanMode = useCallback(() => {
    setIsPanMode(prev => !prev);
  }, []);
  const handlePanChange = useCallback((px: number, py: number) => {
    setCanvasPanX(px);
    setCanvasPanY(py);
  }, []);

  // Toggle clipboard sync
  const toggleClipboard = useCallback(() => {
    if (viewOnlyMode) return;
    setConfig(prev => {
      const newSync = !prev.clipboardSync;
      addToast(newSync ? '剪贴板同步已开启' : '剪贴板同步已关闭', 'info');
      return { ...prev, clipboardSync: newSync };
    });
  }, [addToast, viewOnlyMode]);

  // Toggle fullscreen with keyboard lock for system key capture
  const toggleFullscreen = useCallback(async () => {
    if (!document.fullscreenElement) {
      // Enter fullscreen
      try {
        await document.documentElement.requestFullscreen();

        // Try to lock keyboard for system keys (Alt+Tab, Win, etc.)
        // Only works in fullscreen mode on supported browsers (Chrome 68+)
        if ('keyboard' in navigator && 'lock' in (navigator as Navigator & { keyboard: { lock: (keys?: string[]) => Promise<void> } }).keyboard) {
          try {
            // Lock system keys that would normally exit fullscreen or switch windows
            await (navigator as Navigator & { keyboard: { lock: (keys?: string[]) => Promise<void> } }).keyboard.lock([
              'Escape',       // Don't exit fullscreen
              'Tab',          // Alt+Tab
              'AltLeft',      // Alt key
              'AltRight',
              'MetaLeft',     // Windows key
              'MetaRight',
              'F4',           // Alt+F4
            ]);
            addToast('全屏模式已启用，系统快捷键将发送到远程', 'success');
            // Notify InputService about keyboard lock state
            eventBus.emit('input:keyboardLocked', true);
          } catch (lockErr) {
            console.warn('[App] Keyboard lock failed:', lockErr);
            addToast('全屏模式已启用（快捷键拦截不可用）', 'info');
          }
        } else {
          addToast('全屏模式已启用', 'info');
        }
      } catch (err) {
        console.error('[App] Failed to enter fullscreen:', err);
        addToast('无法进入全屏模式', 'error');
      }
    } else {
      // Exit fullscreen - keyboard will be automatically unlocked
      try {
        await document.exitFullscreen();
        eventBus.emit('input:keyboardLocked', false);
      } catch (err) {
        console.error('[App] Failed to exit fullscreen:', err);
      }
    }
  }, [addToast]);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isNowFullscreen = !!document.fullscreenElement;
      setIsFullscreen(isNowFullscreen);

      // When exiting fullscreen (by any means), ensure keyboard is unlocked
      if (!isNowFullscreen) {
        eventBus.emit('input:keyboardLocked', false);
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Handle file upload
  const handleUploadFiles = useCallback(() => {
    if (connectionState !== ConnectionState.CONNECTED) {
      addToast('请先连接到远程桌面', 'warning');
      return;
    }
    fileInputRef.current?.click();
  }, [connectionState, addToast]);

  // Handle directory upload
  const handleUploadDirectory = useCallback(() => {
    if (connectionState !== ConnectionState.CONNECTED) {
      addToast('请先连接到远程桌面', 'warning');
      return;
    }
    directoryInputRef.current?.click();
  }, [connectionState, addToast]);

  // Handle file/directory input change (shared handler)
  const handleFileInputChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const fileArray: File[] = Array.from(files);
    try {
      await uploadFilesToForegroundDir(fileArray);
    } catch (err) {
      const message = err instanceof Error ? err.message : '未知错误';
      addToast(`文件传输失败: ${message}`, 'error');
      addLog(`文件传输失败: ${message}`, 'error');
    }

    e.target.value = ''; // Reset input
  }, [addToast, addLog]);

  // Handle Ctrl+V paste to upload files
  const handlePaste = useCallback(async (e: ClipboardEvent) => {
    // 只读模式：禁止粘贴上传
    if (viewOnlyMode) return;
    // If FileManager is open, let it handle paste
    if (showFileManager) {
      return;
    }

    // Only handle when connected
    if (connectionState !== ConnectionState.CONNECTED) {
      return;
    }

    const items = e.clipboardData?.items;
    if (!items || items.length === 0) return;

    // IMPORTANT: Collect all data SYNCHRONOUSLY before any async operation!
    // DataTransferItemList and its items become invalid after the event handler returns
    const hasEntryApi = items[0]?.webkitGetAsEntry !== undefined;

    // Collect files or entries synchronously
    let files: File[] = [];
    const entries: FileSystemEntry[] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        if (hasEntryApi) {
          const entry = item.webkitGetAsEntry();
          if (entry) {
            entries.push(entry);
          }
        } else {
          const file = item.getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }
    }

    // Check if we have any files/entries
    if (entries.length === 0 && files.length === 0) {
      // No files in clipboard, let normal paste behavior happen
      return;
    }

    // Prevent default paste behavior when we have files
    e.preventDefault();

    try {
      // If we have entries, process them asynchronously now
      if (entries.length > 0) {
        addToast('正在扫描文件...', 'info');

        // Process entries using the same logic as getAllFiles
        const readAllEntries = async (reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> => {
          const allEntries: FileSystemEntry[] = [];
          let readEntries: FileSystemEntry[];
          do {
            readEntries = await new Promise<FileSystemEntry[]>((resolve, reject) => {
              reader.readEntries(resolve, reject);
            });
            allEntries.push(...readEntries);
          } while (readEntries.length > 0);
          return allEntries;
        };

        const readEntry = async (entry: FileSystemEntry, path: string): Promise<void> => {
          if (entry.isFile) {
            const fileEntry = entry as FileSystemFileEntry;
            const file = await new Promise<File>((resolve, reject) => {
              fileEntry.file(
                (f) => {
                  const fileWithPath = new File([f], f.name, { type: f.type });
                  Object.defineProperty(fileWithPath, 'webkitRelativePath', {
                    value: path + f.name,
                    writable: false,
                  });
                  resolve(fileWithPath);
                },
                reject
              );
            });
            files.push(file);
          } else if (entry.isDirectory) {
            const dirEntry = entry as FileSystemDirectoryEntry;
            const reader = dirEntry.createReader();
            const childEntries = await readAllEntries(reader);
            for (const childEntry of childEntries) {
              await readEntry(childEntry, path + entry.name + '/');
            }
          }
        };

        for (const entry of entries) {
          await readEntry(entry, '');
        }
      }

      if (files.length === 0) {
        addToast('大哥，你选择的是一个空目录，里面啥都没有', 'warning');
        return;
      }

      await uploadFilesToForegroundDir(files);
    } catch (err) {
      const message = err instanceof Error ? err.message : '未知错误';
      addToast(`文件传输失败: ${message}`, 'error');
      addLog(`文件传输失败: ${message}`, 'error');
    }
  }, [connectionState, addToast, addLog, showFileManager, viewOnlyMode]);

  // Listen for Pointer Lock changes (for game mouse mode)
  useEffect(() => {
    const handlePointerLockChange = () => {
      setIsPointerLocked(!!document.pointerLockElement);
    };
    document.addEventListener('pointerlockchange', handlePointerLockChange);
    return () => {
      document.removeEventListener('pointerlockchange', handlePointerLockChange);
    };
  }, []);

  // Calculate actual cursor style
  const actualCursorHidden = config.gameMouseMode;

  // Auto switch between Pointer Lock and normal mode
  // Rule: whenever local cursor is hidden, use relative movement (Pointer Lock)
  useEffect(() => {

    // Cursor hidden → enter Pointer Lock (relative movement)
    if (actualCursorHidden && !document.pointerLockElement && connectionState === ConnectionState.CONNECTED) {
      eventBus.emit('input:setRelativeMode', true);
    }

    // Cursor visible → exit Pointer Lock (absolute movement)
    // Check both pointerLockElement and isPointerLocked state for robustness
    if (!actualCursorHidden && (document.pointerLockElement || isPointerLocked)) {
      document.exitPointerLock();
      eventBus.emit('input:setRelativeMode', false);
    }
    // Note: we include isPointerLocked here to ensure we catch all cases where we need to exit
  }, [actualCursorHidden, connectionState, isPointerLocked, remoteCursorVisible]);

  // Sync clipboard setting to InputService (also re-sync when connection state changes)
  useEffect(() => {
    inputService.setClipboardEnabled(config.clipboardSync);
  }, [config.clipboardSync, connectionState]);

  // 看 / 控 模式：connected 时根据 viewOnlyMode 应用输入和剪贴板状态。
  // 切换开关（toggleViewOnlyMode）时也通过此 effect 重新应用。
  // 关键：InputService 的 keydown handler 在 isActive=false 时会直接 return —
  //       鼠标按下会 auto-activate，但键盘不会。所以"用户切到控"必须显式 activate()，
  //       否则键盘按下没反应。
  // 用 ref 区分"初次连接（viewOnlyMode 初值生效）"和"用户主动切换"：
  //   - 初次连接：仅 setEnabled，不动 isActive（保持桌面端"鼠标点击激活"原行为，避免无意义弹 toast）
  //   - 用户切换：除了 setEnabled，还要 activate/deactivate
  const viewOnlyInitialAppliedRef = useRef(false);
  useEffect(() => {
    if (connectionState !== ConnectionState.CONNECTED) {
      // 断开后重置，下一次连接会被视为"初次"
      viewOnlyInitialAppliedRef.current = false;
      return;
    }
    const isUserToggle = viewOnlyInitialAppliedRef.current;
    viewOnlyInitialAppliedRef.current = true;

    // 注意：viewOnly 切换不动 clipboardSync —— 拉模式下剪贴板文件查询由前端 Ctrl+C / 手动按钮触发，
    // 与"控制"无关；强制改 clipboardSync 会让 host 端 ClipboardHandler 文本同步也一起关闭，
    // 切回"控"后用户感觉"剪贴板坏了"，所以保持原状态。
    if (viewOnlyMode) {
      setInputEnabled(false);
      inputService.setEnabled({ mouse: false, keyboard: false });
      if (isUserToggle) inputService.deactivate();
    } else {
      setInputEnabled(true);
      inputService.setEnabled({ mouse: true, keyboard: true });
      if (isUserToggle) inputService.activate();
    }
  }, [viewOnlyMode, connectionState]);

  // FileManager open/close auto-toggles control mode, so FM shortcuts (Ctrl+C/V/A...) work locally
  useEffect(() => {
    inputService.setFileManagerOpen(showFileManager);
    // 同步窗口开关到文件管理器 store，供搜索结果导航判断是否需要新开窗口
    useFileManagerStore.getState().setFileManagerOpen(showFileManager);
  }, [showFileManager]);

  // 外部请求打开/关闭文件管理器窗口（搜索结果「打开所在文件夹」/ 初始化中查看进度 / 远程打开成功后关闭）
  useEffect(() => {
    const unsubOpen = eventBus.on('fm:open', () => setShowFileManager(true));
    const unsubClose = eventBus.on('fm:close', () => setShowFileManager(false));
    return () => { unsubOpen(); unsubClose(); };
  }, []);

  // 全局搜索框 / 初始化弹窗打开时暂停远程键鼠转发，避免在输入框打字时按键被发往远程
  useEffect(() => {
    inputService.setModalOpen(isGlobalSearchOpen || isSearchEngineInitModalOpen);
  }, [isGlobalSearchOpen, isSearchEngineInitModalOpen]);

  // Handle video ready
  const handleVideoReady = useCallback((video: HTMLVideoElement) => {
    videoRef.current = video;
  }, []);

  // Ensure video ref is set on mount
  useEffect(() => {
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      const video = document.querySelector('video');
      if (video && !videoRef.current) {
        videoRef.current = video as HTMLVideoElement;
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Subscribe to events
  useEffect(() => {
    const unsubState = eventBus.on('connection:stateChange', ({ state, previousState, pc }) => {
      setConnectionState(state as ConnectionState);
      addLog(`连接状态: ${previousState} -> ${state}`, 'info');

      if (state === ConnectionState.CONNECTED) {
        addLog('安全 P2P 链路已建立', 'success');
        setIsUiVisible(false);
        setHostWaitingInfo(null); // Clear host waiting state on successful connection

        // Extract base URL from WebSocket URL for API calls
        if (currentServerRef.current) {
          try {
            const wsUrl = new URL(currentServerRef.current.wsUrl);
            const protocol = wsUrl.protocol === 'wss:' ? 'https:' : 'http:';
            setServerBaseUrl(`${protocol}//${wsUrl.host}`);
          } catch {
            setServerBaseUrl('');
          }
        }

        // Start stats monitoring with server URL and video element for jitter catchup
        if (pc && currentServerRef.current) {
          statsService.start(pc as RTCPeerConnection, currentServerRef.current.wsUrl, videoRef.current || undefined);
          // Initialize file transfer service with PeerConnection
          fileTransferService.initialize(pc as RTCPeerConnection);
        } else {
          console.warn('[App] Cannot start stats: pc=', !!pc, 'server=', currentServerRef.current);
        }

        // Send initial cursor_config to Host (default: hide system cursor)
        inputService.sendCursorConfig(!config.gameMouseMode);

        // Start status bar auto-hide timer (1 minute after connection)
        connectedTimeRef.current = Date.now();
        setStatusBarAutoHidden(false);
        if (statusBarTimerRef.current) {
          clearTimeout(statusBarTimerRef.current);
        }
        statusBarTimerRef.current = window.setTimeout(() => {
          setStatusBarAutoHidden(true);
          // Notify services to reduce polling when status bar is hidden
          eventBus.emit('statusBar:hidden', {});
        }, STATUS_BAR_AUTO_HIDE_DELAY_MS);
      } else if (state === ConnectionState.DISCONNECTED || state === ConnectionState.FAILED) {
        // Clear status bar timer on disconnect
        if (statusBarTimerRef.current) {
          clearTimeout(statusBarTimerRef.current);
          statusBarTimerRef.current = null;
        }
        setStatusBarAutoHidden(false);
      }
    });

    const unsubReconnect = eventBus.on('connection:reconnecting', ({ attempt }) => {
      setReconnectAttempt(attempt);
      addLog(`正在重连... (${attempt}/10)`, 'warn');
    });

    const unsubError = eventBus.on('connection:error', ({ error }) => {
      addLog(`连接错误: ${error.message}`, 'error');
    });

    const unsubStats = eventBus.on('stats:update', () => {
      setStats(statsService.currentStats);
      setChartData(statsService.getChartData());
    });

    const unsubWarning = eventBus.on('stats:warning', ({ message }) => {
      addToast(message, 'warning');
    });

    // HOST 编码器目标码率更新
    const unsubEncoderBitrate = eventBus.on('stats:encoderBitrate', ({ bitrate }) => {
      setEncoderBitrate(bitrate);
    });

    // Relay peer timeout (1 minute wait)
    const unsubPeerTimeout = eventBus.on('relay:peerTimeout', () => {
      addLog('等待主机超时（1分钟），连接失败', 'error');
      addToast('主机未上线，连接超时', 'error');
      setHostWaitingInfo(null);
    });

    // Host waiting：统一退避重连各次失败时触发。按 reason 分流：
    //   - 'host_offline'（应用层）：信令活着 + host 暂未在线 → 显示"等待主机上线"覆盖（保留原 UX）
    //   - 其他（网络层）：不切 Overlay 主状态，attempt>5 才显示柔和副文案，避免初次抖动闪烁
    const SIGNALING_RETRY_HINT_THRESHOLD = 5;
    const unsubHostWaiting = eventBus.on('host:waiting', ({ attempt, reason }) => {
      if (attempt === 1) {
        addLog(reason === 'host_offline' ? '主机未上线，等待中...' : '信令断开，自动重连中...', 'info');
        // 新一轮重连周期开始，先清掉两边状态再决定显示哪种
        setSignalingRetryAttempt(null);
        setHostWaitingInfo(null);
      }

      if (reason === 'host_offline') {
        // 应用层 host_offline：每次都更新（保持覆盖可见），maxAttempts=0 让倒计时不显示
        setHostWaitingInfo({ attempt, maxAttempts: 0 });
        return;
      }

      // 网络层失败：不切 Overlay 状态，阈值后才在副文案位置显示
      if (attempt > SIGNALING_RETRY_HINT_THRESHOLD) {
        setSignalingRetryAttempt(attempt);
      }
    });

    // 信令重连成功 → 清掉网络层副文案（应用层 hostWaitingInfo 由 peer_status:online 单独清）
    const unsubSignalingOpen = eventBus.on('signaling:open', () => {
      setSignalingRetryAttempt(null);
    });

    // host 真正上线 → 清空"等待主机上线"覆盖
    const unsubPeerStatus = eventBus.on('peer:status', ({ status, peerType }) => {
      if (peerType === 'Host' && status === 'online') {
        setHostWaitingInfo(null);
      }
    });

    // Host offline (legacy 路径，兜底清空)
    const unsubHostOffline = eventBus.on('host:offline', () => {
      setHostWaitingInfo(null);
      setSignalingRetryAttempt(null);
    });

    // Capture access denied (MSTSC minimized)
    const unsubCaptureAccessDenied = eventBus.on('capture:accessDenied', ({ message }) => {
      addLog(`远程桌面截图受阻: ${message}`, 'warn');
      setCaptureAccessDenied(true);
    });

    // Capture access restored
    const unsubCaptureAccessRestored = eventBus.on('capture:accessRestored', ({ message }) => {
      addLog(`远程桌面截图恢复: ${message}`, 'success');
      setCaptureAccessDenied(false);
    });

    const unsubToast = eventBus.on('ui:toast', ({ message, type }) => {
      addToast(message, type as Toast['type']);
    });

    // File transfer progress
    const unsubFileTransfer = eventBus.on('fileTransfer:update', (data) => {
      setFileTransferProgress({
        id: data.id,
        fileName: data.fileName,
        totalFiles: data.totalFiles,
        totalSize: data.totalSize,
        transferredBytes: data.transferredBytes,
        status: data.status,
        startTime: data.startTime,
        files: data.files || [],
      });
      // Show panel when transfer starts
      setShowFileTransferPanel(true);
      // Update transferring state for status bar visibility
      setIsTransferring(data.status === 'transferring');

      // Auto-close panel when transfer is done (after delay for user to see result)
      if (data.status === 'completed' || data.status === 'cancelled' || data.status === 'failed') {
        setTimeout(() => {
          setShowFileTransferPanel(false);
          setFileTransferProgress(null);
        }, 2000);
      }
    });

    // Cursor image cache
    const cursorCache = new Map<number, string>();

    const unsubCursor = eventBus.on('input:cursor', (data: { cursor?: {
      cursorCSS?: string;
      visible?: boolean;
      cursorId?: number;
      imageData?: string;
      width?: number;
      height?: number;
      hotspotX?: number;
      hotspotY?: number;
    } }) => {
      if (!data.cursor) return;

      const cursor = data.cursor;

      // [临时诊断 CursorDbg] 定位 NewWeb 为何不渲染 host 推的光标形状：
      // 看 hover 时收到的消息有没有 imageData、缓存是否命中、最终设了什么 cursor。
      console.log('[CursorDbg] hasImg=', !!cursor.imageData, 'w=', cursor.width, 'h=', cursor.height,
        'id=', cursor.cursorId,
        'cachedBefore=', cursor.cursorId ? cursorCache.has(cursor.cursorId) : false,
        'visible=', cursor.visible);

      // Cache custom cursor image
      if (cursor.imageData && cursor.width && cursor.height && cursor.cursorId) {
        try {
          // Convert BGRA to RGBA and create data URL
          const imageBytes = atob(cursor.imageData);
          const rgba = new Uint8ClampedArray(imageBytes.length);
          for (let i = 0; i < imageBytes.length; i += 4) {
            rgba[i] = imageBytes.charCodeAt(i + 2);     // R <- B
            rgba[i + 1] = imageBytes.charCodeAt(i + 1); // G
            rgba[i + 2] = imageBytes.charCodeAt(i);     // B <- R
            rgba[i + 3] = imageBytes.charCodeAt(i + 3); // A
          }

          // Create canvas and draw image
          const canvas = document.createElement('canvas');
          canvas.width = cursor.width;
          canvas.height = cursor.height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            const imageData = new ImageData(rgba, cursor.width, cursor.height);
            ctx.putImageData(imageData, 0, 0);
            const dataUrl = canvas.toDataURL('image/png');
            cursorCache.set(cursor.cursorId, dataUrl);
          }
        } catch (e) {
          console.error('[App] Cursor image decode failed:', e);
        }
      }

      // Update visibility state
      setRemoteCursorVisible(!!cursor.visible);

      if (!cursor.visible) {
        setRemoteCursor('none');
        return;
      }

      // Use cached custom cursor image
      const cachedUrl = cursor.cursorId ? cursorCache.get(cursor.cursorId) : null;
      if (cachedUrl) {
        const hx = cursor.hotspotX || 0;
        const hy = cursor.hotspotY || 0;
        setRemoteCursor(`url(${cachedUrl}) ${hx} ${hy}, auto`);
      } else if (cursor.cursorCSS) {
        setRemoteCursor(cursor.cursorCSS);
      } else {
        // Default cursor when visible but no specific style
        setRemoteCursor('default');
      }
    });

    // Helper to try playing audio (handles autoplay policy)
    const tryPlayAudio = (audioEl: HTMLAudioElement | null, name: string) => {
      if (!audioEl) return;
      audioEl.play().catch(() => {});
    };

    // System audio track handler
    const unsubSystemAudio = eventBus.on('audio:systemTrack', (stream: MediaStream) => {
      if (systemAudioRef.current) {
        systemAudioRef.current.srcObject = stream;
        tryPlayAudio(systemAudioRef.current, 'System audio');
      }
    });

    // Microphone audio track handler
    const unsubMicAudio = eventBus.on('audio:micTrack', (stream: MediaStream) => {
      if (micAudioRef.current) {
        micAudioRef.current.srcObject = stream;
        tryPlayAudio(micAudioRef.current, 'Mic audio');
      }
    });

    // User interaction handler to resume audio (for autoplay policy)
    const handleUserInteraction = () => {
      tryPlayAudio(systemAudioRef.current, 'System audio (user interaction)');
      tryPlayAudio(micAudioRef.current, 'Mic audio (user interaction)');
    };
    document.addEventListener('click', handleUserInteraction, { once: true });
    document.addEventListener('keydown', handleUserInteraction, { once: true });

    // Force show cursor handler (ESC long press)
    const unsubForceShowCursor = eventBus.on('input:forceShowCursor', () => {
      setRemoteCursorVisible(true);
      setRemoteCursor('default');
      addToast('已强制显示光标', 'info');
    });

    // Game mouse mode toggle handler (from SettingsPanel or Ctrl+ESC)
    const unsubGameMouseMode = eventBus.on('input:gameMouseMode', (enabled: boolean) => {
      setConfig(prev => ({ ...prev, gameMouseMode: enabled }));
      inputService.setGameMouseMode(enabled);

      if (enabled) {
        // ON: hide local cursor, show remote cursor, relative movement
        eventBus.emit('input:setRelativeMode', true);
        inputService.sendCursorConfig(false);  // Host 显示系统光标
        setShowSettings(false);   // 自动关闭会话配置面板
        setShowStats(false);      // 自动关闭引擎诊断面板
        addToast('游戏鼠标模式已开启，Alt+F1 退出', 'info');
      } else {
        // OFF: show local cursor, hide remote cursor, absolute movement
        eventBus.emit('input:setRelativeMode', false);
        inputService.sendCursorConfig(true);  // Host 隐藏系统光标
        addToast('游戏鼠标模式已关闭', 'info');
      }
    });

    // P2P assist state changes
    const unsubP2PAssist = eventBus.on('p2p_assist:stateChange', (state: {
      isRunning: boolean;
      bestAttempt: { result?: { throughputKbps: number } } | null;
    }) => {
      setIsP2PAssistRunning(state.isRunning);
      setHasBestP2PChannel(!!state.bestAttempt);
    });

    // 主连接切换完成后自动关闭文件管理器（通道已失效，重新打开时懒创建新通道）
    const unsubConnectionSwitched = eventBus.on('connection:switched', () => {
      setShowFileManager(false);
    });

    // 系统自动切换后重置 ICE/TURN 设置为自动（丢包重连、P2P Assist 切换等）
    const unsubConfigAutoReset = eventBus.on('config:autoReset', () => {
      setConfig(prev => ({ ...prev, icePolicy: 'auto', turnProtocol: 'auto' }));
    });

    // host 端推送的全局配置状态（连接建立 / 其他客户端改了配置 / 本客户端改完后广播）
    // 不持久化到 localStorage：以 host 实际状态为准
    const unsubServerConfigStatus = eventBus.on('config:server_status', (data: {
      fixedBitrate?: { enabled: boolean; nvencKbps: number; swKbps: number };
      screenCapturer?: typeof screenCapturer;
    }) => {
      if (data.fixedBitrate) {
        setConfig(prev => ({
          ...prev,
          fixedBitrateEnabled: data.fixedBitrate!.enabled,
          fixedBitrateNvencKbps: data.fixedBitrate!.nvencKbps,
          fixedBitrateSwKbps: data.fixedBitrate!.swKbps,
        }));
      }
      if (data.screenCapturer) {
        setScreenCapturer(data.screenCapturer);
      }
    });

    // Host crash info received
    const unsubCrashInfo = eventBus.on('host:crashInfo', (data: { content: string }) => {
      setCrashInfo(data.content);
      addToast('Host crashed before connection - check notification', 'warning');
    });

    // Connected peers list update (for status bar display)
    const unsubPeersUpdate = eventBus.on('peers:update', (data: { peers: PeerInfo[]; total: number }) => {
      setConnectedPeers(data.peers);
    });

    // PC reference updated (e.g., P2P upgrade) - reinitialize FileTransferService
    const unsubPcUpdated = eventBus.on('connection:pcUpdated', ({ pc }: { pc: RTCPeerConnection }) => {
      fileTransferService.initialize(pc);
    });

    // 页面关闭/刷新时主动断开，让服务器立即感知（而非等 60s Pong 超时）
    const handleBeforeUnload = () => {
      webRTCService.disconnect();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      unsubState();
      unsubReconnect();
      unsubError();
      unsubStats();
      unsubWarning();
      unsubEncoderBitrate();
      unsubPeerTimeout();
      unsubHostWaiting();
      unsubSignalingOpen();
      unsubPeerStatus();
      unsubHostOffline();
      unsubCaptureAccessDenied();
      unsubCaptureAccessRestored();
      unsubToast();
      unsubCursor();
      unsubFileTransfer();
      unsubSystemAudio();
      unsubMicAudio();
      unsubForceShowCursor();
      unsubGameMouseMode();
      unsubP2PAssist();
      unsubConnectionSwitched();
      unsubConfigAutoReset();
      unsubServerConfigStatus();
      unsubCrashInfo();
      unsubPeersUpdate();
      unsubPcUpdated();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [addLog, addToast]);

  // Keep UI visible when panels are open
  useEffect(() => {
    if (showSettings || showStats || showQuickActions) {
      setIsUiVisible(true);
    }
  }, [showSettings, showStats, showQuickActions]);

  // 页面加载后用 merchant_id / terminalid 获取网吧信息（仅一次）
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const merchantId = params.get('merchant_id');
    const terminalId = params.get('terminalid');
    if (!merchantId) return;
    // 网吧名称 + 分组（/merchant/{id}）
    (async () => {
      try {
        const resp = await apiClient.request<{ data?: { merchant?: { name?: string; groups?: { name?: string }[] } } }>(
          `/merchant/${merchantId}`,
          { method: 'GET', headers: { 'X-HTTP-Method-Override': 'PUT', 'Accept': 'application/json' } },
        );
        const merchant = resp?.data?.merchant;
        const name = merchant?.name;
        if (!name) return;
        const group = merchant?.groups?.[0]?.name || '';
        setVenueName(name);
        setVenueGroup(group);
      } catch {
        // 获取失败静默，保留默认 title
      }
    })();
    // 终端座位号：/terminals?merchant_id= 列表里找 id==terminalid 的 seat
    if (terminalId) {
      (async () => {
        try {
          const resp = await apiClient.request<{ data?: { id?: number; seat?: string; ip?: string; name?: string }[] }>(
            `/terminals?merchant_id=${merchantId}`,
            { method: 'GET', headers: { 'Accept': 'application/json' } },
          );
          const hit = (resp?.data || []).find((t) => String(t.id) === terminalId);
          if (hit?.seat) setTerminalSeat(hit.seat);
          if (hit?.ip) setTerminalIp(hit.ip);
          if (hit?.name) setTerminalName(hit.name);
        } catch {
          // 获取失败静默
        }
      })();
    }
  }, []);

  // 网页 title：网吧名称|网吧备注或座位号|IP（备注 name 非空用备注，否则用座位号 seat）
  useEffect(() => {
    const terminalLabel = terminalName || terminalSeat;
    const parts = [venueName, terminalLabel, terminalIp].filter(Boolean);
    if (parts.length) document.title = parts.join('|');
  }, [venueName, terminalName, terminalSeat, terminalIp]);

  // Auto-hide toolbar after 10 seconds when panels closed and mouse not in dock
  const autoHideTimerRef = useRef<number | null>(null);
  useEffect(() => {
    // Clear any existing timer
    if (autoHideTimerRef.current) {
      window.clearTimeout(autoHideTimerRef.current);
      autoHideTimerRef.current = null;
    }

    // Only process when toolbar is visible and connected
    if (!isUiVisible || connectionState !== ConnectionState.CONNECTED) return;

    // If any panel is open or mouse in dock, don't start timer
    if (showSettings || showStats || showQuickActions) {
      return;
    }

    // Start 10 second timer to auto-hide
    autoHideTimerRef.current = window.setTimeout(() => {
      setIsUiVisible(false);
    }, 10000);

    return () => {
      if (autoHideTimerRef.current) {
        window.clearTimeout(autoHideTimerRef.current);
        autoHideTimerRef.current = null;
      }
    };
  }, [isUiVisible, connectionState, showSettings, showStats, showQuickActions]);

  // Control stats polling based on status bar / stats panel visibility
  useEffect(() => {
    if (connectionState === ConnectionState.CONNECTED) {
      // Enable stats when status bar is visible OR stats panel is open
      const needStats = showStats || config.statusBarVisibility === 'on' ||
        (config.statusBarVisibility === 'auto' && !statusBarAutoHidden);

      if (needStats) {
        statsService.setEnabled(true);
        // Notify backend to start sending detailed stats only when stats panel is open
        webRTCService.sendStatsConfig(showStats);
      } else {
        statsService.setEnabled(false);
        // Notify backend to stop sending stats
        webRTCService.sendStatsConfig(false);
      }
    }
  }, [showStats, connectionState, config.statusBarVisibility, statusBarAutoHidden]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Block browser Ctrl shortcuts
    if (e.ctrlKey) {
      const key = e.key.toLowerCase();
      if (['s', 'n', 'o', 't', 'w', 'r', 'p'].includes(key)) {
        e.preventDefault();
      }
    }

    // F-key shortcuts (only when connected)
    if (connectionState === ConnectionState.CONNECTED) {
      switch (e.key) {
        case 'F8':
          e.preventDefault();
          // Toggle status bar visibility
          setConfig(prev => ({
            ...prev,
            statusBarVisibility: prev.statusBarVisibility === 'on' ? 'off' : 'on'
          }));
          break;
        case 'F9':
          e.preventDefault();
          setShowSettings(prev => !prev);
          break;
        case 'F10':
          e.preventDefault();
          setShowStats(prev => !prev);
          break;
        case 'F11':
          e.preventDefault();
          toggleFullscreen();
          break;
      }

      // Alt + / Alt - : 以鼠标位置为中心缩放画布
      if (e.altKey) {
        if (e.key === '=' || e.key === '+') {
          e.preventDefault();
          handleZoomAtPoint(true);
        } else if (e.key === '-') {
          e.preventDefault();
          handleZoomAtPoint(false);
        }
      }
    }
  }, [connectionState, toggleFullscreen, handleZoomAtPoint]);

  // Alt + 滚轮：以鼠标位置为中心缩放
  const handleWheel = useCallback((e: WheelEvent) => {
    if (!e.altKey) return;
    e.preventDefault();
    handleZoomAtPoint(e.deltaY < 0);
  }, [handleZoomAtPoint]);

  // Alt + 中键点击：重置画布
  const handleMiddleClick = useCallback((e: MouseEvent) => {
    if (e.altKey && e.button === 1) {
      e.preventDefault();
      handleResetZoom();
    }
  }, [handleResetZoom]);

  // Block browser defaults and handle paste for file upload
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => e.preventDefault();

    window.addEventListener('contextmenu', handleContextMenu);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('mousedown', handleMiddleClick);
    window.addEventListener('paste', handlePaste);

    return () => {
      window.removeEventListener('contextmenu', handleContextMenu);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('mousedown', handleMiddleClick);
      window.removeEventListener('paste', handlePaste);
    };
  }, [handlePaste, handleKeyDown, handleWheel, handleMiddleClick]);

  const isConnected = connectionState === ConnectionState.CONNECTED;

  // Help page: ?help
  if (window.location.search === '?help') {
    return (
      <div className="w-screen h-screen bg-[#0a0a0c] text-slate-200 flex items-center justify-center p-8">
        <div className="bg-[#202020] border border-white/10 rounded-lg shadow-2xl w-[600px] max-h-[80vh] overflow-auto">
          <div className="bg-[#2d2d2d] px-6 py-4 border-b border-white/5 rounded-t-lg">
            <h1 className="text-lg font-medium">{'\u652F\u6301\u7684 URL \u53C2\u6570'}</h1>
          </div>
          <div className="p-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 border-b border-white/5">
                  <th className="pb-3 pr-4">{'\u53C2\u6570'}</th>
                  <th className="pb-3 pr-4">{'\u503C'}</th>
                  <th className="pb-3">{'\u8BF4\u660E'}</th>
                </tr>
              </thead>
              <tbody className="text-slate-300">
                {[
                  ['peer', 'string', '\u8981\u8FDE\u63A5\u7684 Host ID'],
                  ['relay', 'url', '\u4FE1\u4EE4\u670D\u52A1\u5668\u5730\u5740'],
                  ['mode', 'file-manager', '\u72EC\u7ACB\u6587\u4EF6\u7BA1\u7406\u5668\u6A21\u5F0F'],
                  ['codec', 'h264 | h265', '\u5F3A\u5236\u89C6\u9891\u7F16\u89E3\u7801\u5668'],
                  ['p2pPool', 'number', 'P2P \u5019\u9009\u6C60\u6570\u91CF'],
                  ['disableTurn', '1', '\u7981\u7528 TURN \u4E2D\u7EE7'],
                  ['forceRelay', '1', '\u5F3A\u5236\u4F7F\u7528 TURN \u4E2D\u7EE7\u6A21\u5F0F'],
                  ['lanOnly', '1', '\u5C40\u57DF\u7F51\u4E13\u7528\u76F4\u8FDE\uFF08\u4EC5 host \u5019\u9009\uFF0C\u4E0D\u8D70 STUN/TURN\uFF09'],
                  ['lang', 'en | zh', '\u6587\u4EF6\u7BA1\u7406\u5668\u8BED\u8A00'],
                  ['isdebug', '1', '\u8C03\u8BD5\u6A21\u5F0F\uFF08\u7A7A\u95F2\u68C0\u6D4B 5 \u79D2\uFF09'],
                  ['viewonly', 'true | false', '\u53EA\u8BFB\u521D\u503C\uFF08\u9ED8\u8BA4 true\uFF09\uFF1B\u8FDE\u63A5\u540E\u53EF\u5728\u72B6\u6001\u680F\u5F00\u5173\u968F\u65F6\u5207\u6362\u3002false=\u9ED8\u8BA4\u53EF\u63A7'],
                ].map(([param, value, desc]) => (
                  <tr key={param} className="border-b border-white/5">
                    <td className="py-2.5 pr-4"><code className="text-blue-300">{param}</code></td>
                    <td className="py-2.5 pr-4 text-slate-500">{value}</td>
                    <td className="py-2.5 text-slate-400">{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-6 text-xs text-slate-500">
              {'\u793A\u4F8B\uFF1A'}<code className="text-slate-400">?peer=mine&codec=h265</code>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Standalone File Manager Mode
  if (showFileManager && new URLSearchParams(window.location.search).get('mode') === 'file-manager') {
    return (
      <div className="w-screen h-screen bg-win-bg overflow-hidden relative">
        {/* Background Orbs to match WebFileManager */}
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-blue-600/20 blur-[120px] rounded-full pointer-events-none" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-purple-600/20 blur-[120px] rounded-full pointer-events-none" />
        
        {isConnected ? (
          <FileManager peerId={connectionName || undefined} />
        ) : (
          <ConnectionOverlay
            state={connectionState}
            logs={logs}
            reconnectAttempt={reconnectAttempt}
            maxAttempts={RECONNECT_STRATEGY.maxAttempts}
            hostWaitingInfo={hostWaitingInfo}
            signalingRetryAttempt={signalingRetryAttempt}
            onConnectRelay={connectRelay}
            onCancel={disconnect}
          />
        )}
        <ToastContainer toasts={toasts} />
        <UploadManager />
        <ConflictModal />
        <GlobalSearchPanel />
        <SearchEngineInitModal
          isOpen={isSearchEngineInitModalOpen}
          onClose={closeSearchEngineInitModal}
          isReinit={isSearchEngineReinit}
        />
      </div>
    );
  }

  // 未授权：覆盖所有主界面（连 ConnectionOverlay 都不渲染，无法发起连接）
  if (!isAuthorized) {
    return (
      <>
        <UnauthorizedScreen />
        <ToastContainer toasts={toasts} />
      </>
    );
  }

  return (
    <div
      className="relative w-screen h-screen bg-black overflow-hidden"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag Overlay */}
      {isDragging && isConnected && (
        <div className="absolute inset-0 z-[200] bg-blue-500/20 backdrop-blur-sm flex items-center justify-center pointer-events-none">
          <div className="bg-gray-900/90 rounded-2xl p-8 shadow-2xl border-2 border-blue-400 border-dashed">
            <div className="text-center">
              <svg className="w-16 h-16 mx-auto mb-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-xl font-medium text-white mb-1">拖放文件到远程桌面</p>
              <p className="text-sm text-gray-400">文件将被复制到远程剪贴板</p>
            </div>
          </div>
        </div>
      )}

      {/* File Transfer Panel */}
      {fileTransferProgress && showFileTransferPanel && !viewOnlyMode && (
        <FileTransferPanel
          transfer={fileTransferProgress}
          onClose={() => {
            setShowFileTransferPanel(false);
            // Clear progress after closing if completed/failed
            if (fileTransferProgress.status === 'completed' || fileTransferProgress.status === 'failed') {
              setFileTransferProgress(null);
            }
          }}
        />
      )}

      {/* Clipboard Files Download Notification (Toast, 拉模式) */}
      {!viewOnlyMode && <ClipboardFilesNotification />}

      {/* Hidden Audio Elements for remote audio playback */}
      <audio ref={systemAudioRef} autoPlay hidden muted={!config.systemAudioEnabled} />
      <audio ref={micAudioRef} autoPlay hidden muted={!config.micAudioEnabled} />

      {/* Video Player */}
      <VideoPlayer
        isActive={isConnected}
        isBlurred={showSettings}
        inputEnabled={inputEnabled}
        cursorStyle={
          // 光标隐藏时显示 'none'，否则显示远程光标样式
          actualCursorHidden ? 'none' : (remoteCursor !== 'none' ? remoteCursor : 'default')
        }
        gameMode={config.gameMouseMode}
        onVideoReady={handleVideoReady}
        zoom={canvasZoom}
        panX={canvasPanX}
        panY={canvasPanY}
        isPanMode={isPanMode}
        onPanChange={handlePanChange}
      />

      {/* MSTSC Minimized Overlay */}
      {isConnected && captureAccessDenied && !viewOnlyMode && (
        <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-zinc-900 border border-yellow-500/30 rounded-2xl p-8 max-w-md mx-4 text-center space-y-6">
            <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>

            <div>
              <h3 className="text-xl font-bold text-white mb-2">远程桌面已最小化</h3>
              <p className="text-zinc-400 text-sm">
                远程 MSTSC 窗口可能已最小化，无法获取到最新图像。
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  // 切换到登录界面模式 - 发送请求给 Host 切换到 GDI 模式
                  signalingService.sendSwitchToLoginScreen();
                  setCaptureAccessDenied(false);
                }}
                className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-colors"
              >
                切换到登录界面
              </button>
              <button
                onClick={() => {
                  // 请求Host重新启动截图
                  signalingService.sendRestartCapture();
                  setCaptureAccessDenied(false);
                }}
                className="w-full py-3 px-4 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-medium rounded-xl transition-colors border border-zinc-700"
              >
                等待最大化
              </button>
            </div>

            <p className="text-xs text-zinc-500">
              「切换到登录界面」将使用 GDI 模式捕获 Windows 登录/锁屏界面。
              「等待最大化」将等待远程窗口恢复后自动继续显示画面。
            </p>
          </div>
        </div>
      )}

      {/* Top Status Bar */}
      {isConnected && (() => {
        // Calculate status bar visibility based on config
        let shouldShowStatusBar = false;
        switch (config.statusBarVisibility) {
          case 'on':
            shouldShowStatusBar = true;
            break;
          case 'off':
            shouldShowStatusBar = false;
            break;
          case 'auto':
          default:
            // Show if: not auto-hidden yet, or network issues, or transferring
            shouldShowStatusBar = !statusBarAutoHidden || stats.packetLoss > 2 || stats.jitter > 50 || isTransferring;
            break;
        }
        return shouldShowStatusBar ? (
          <StatusBar
            stats={stats}
            isVisible={true}
            clipboardSync={config.clipboardSync}
            onToggleClipboard={toggleClipboard}
            onToggleQuickActions={() => setShowQuickActions(!showQuickActions)}
            onUploadFiles={handleUploadFiles}
            onOpenUploadPanel={() => useFileManagerStore.getState().openUploadManager()}
            onUploadDirectory={handleUploadDirectory}
            onDownloadClipboard={async () => {
              // 拉模式：主动查询，命中则 emit filesAvailable 触发 Toast
              try {
                const r = await clipboardQueryService.query('manual');
                if (r.hasFiles && r.files.length > 0) {
                  eventBus.emit('clipboard:filesAvailable', {
                    files: r.files,
                    totalSize: r.totalSize,
                    fileCount: r.fileCount,
                  });
                } else {
                  eventBus.emit('ui:toast', { message: '剪贴板中没有可下载的文件', type: 'info' });
                }
              } catch {
                eventBus.emit('ui:toast', { message: '剪贴板查询失败', type: 'warning' });
              }
            }}
            onToggleFullscreen={toggleFullscreen}
            quickActionsActive={showQuickActions}
            isTransferring={isTransferring || fmUploadCount > 0}
            uploadingCount={fmUploadCount}
            isFullscreen={isFullscreen}
            onToggleFileManager={() => setShowFileManager(!showFileManager)}
            fileManagerActive={showFileManager}
            connectedPeers={connectedPeers}
            targetBitrate={encoderBitrate > 0 ? encoderBitrate / 1000 : 0}
            orientation={config.statusBarOrientation}
            onToggleStats={() => setShowStats(!showStats)}
            statsActive={showStats}
            onToggleIceRacing={() => setShowP2PAssistPanel(!showP2PAssistPanel)}
            iceRacingActive={showP2PAssistPanel}
            onOpenSettings={() => setShowSettings(!showSettings)}
            settingsActive={showSettings}
            onDisconnect={disconnect}
            inputEnabled={inputEnabled}
            onToggleInput={toggleInput}
            canvasZoom={canvasZoom}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            isPanMode={isPanMode}
            onTogglePanMode={handleTogglePanMode}
            onResetZoom={handleResetZoom}
            peerId={connectionName || undefined}
            venueName={venueName}
            venueGroup={venueGroup}
            terminalSeat={terminalName || terminalSeat}
            terminalIp={terminalIp}
            viewOnly={viewOnlyMode}
            onToggleViewOnly={toggleViewOnlyMode}
            noauth={isNoauthMode}
          />
        ) : null;
      })()}

      {/* Quick Actions Panel */}
      {showQuickActions && isConnected && !viewOnlyMode && (
        <QuickActions
          onClose={() => setShowQuickActions(false)}
          onAction={handleQuickAction}
        />
      )}

      {/* Connection Overlay (when not connected) */}
      {!isConnected && (
        <ConnectionOverlay
          state={connectionState}
          logs={logs}
          reconnectAttempt={reconnectAttempt}
          maxAttempts={RECONNECT_STRATEGY.maxAttempts}
          hostWaitingInfo={hostWaitingInfo}
          signalingRetryAttempt={signalingRetryAttempt}
          onConnectRelay={connectRelay}
          onCancel={disconnect}
        />
      )}

      {/* Stats Panel */}
      {showStats && isConnected && (
        <StatsPanel
          stats={stats}
          chartData={chartData}
          onClose={() => setShowStats(false)}
          onExportReport={() => {
            const report = statsService.generateReport();
            if (navigator.clipboard?.writeText) {
              navigator.clipboard.writeText(report).then(() => {
                addToast('诊断报告已复制到剪贴板', 'success');
              });
            } else {
              const textarea = document.createElement('textarea');
              textarea.value = report;
              textarea.style.position = 'fixed';
              textarea.style.opacity = '0';
              document.body.appendChild(textarea);
              textarea.select();
              document.execCommand('copy');
              document.body.removeChild(textarea);
              addToast('诊断报告已复制到剪贴板', 'success');
            }
          }}
          isSettingsOpen={showSettings}
          encoderBitrate={encoderBitrate}
          onExpand={() => setShowDashboard(true)}
          onOpenIceDetail={() => setShowIceDetail(true)}
        />
      )}

      {/* Stage 2C: 完整诊断 Dashboard（全屏 Modal）*/}
      <DiagnosticDashboard
        open={showDashboard}
        stats={stats}
        chartData={chartData}
        onClose={() => setShowDashboard(false)}
      />

      {/* ICE 详情回看浮层（从引擎诊断报告 StatsPanel 打开）*/}
      {showIceDetail && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-8 bg-black/50" onClick={() => setShowIceDetail(false)}>
          <div className="w-full max-w-xl" onClick={(e) => e.stopPropagation()}>
            <IceRacingPanel
              snapshot={iceRacingService.lastRaceSnapshot}
              clientTurn={stats.relayServer}
              hostTurn={statsService.serverStats?.host_relay_server}
              relayProtocol={stats.relayProtocol}
              transportType={stats.transportType}
              onClose={() => setShowIceDetail(false)}
            />
          </div>
        </div>
      )}


      {/* P2P Assist Panel */}
      <P2PAssistPanel
        isOpen={showP2PAssistPanel}
        onClose={() => setShowP2PAssistPanel(false)}
      />


      {/* Settings Panel */}
      <SettingsPanel
        isOpen={showSettings}
        config={config}
        onConfigChange={setConfig}
        onClose={() => setShowSettings(false)}
        hostPlatform={stats.hostPlatform}
        availableResolutions={stats.availableResolutions}
        currentResolution={stats.resolution}
        encoderName={stats.encoderName}
        viewOnly={viewOnlyMode}
        screenCapturer={screenCapturer}
      />

      {/* ControlBar removed - functionality merged into StatusBar */}

      {/* File Manager Overlay */}
      {showFileManager && (
        <div 
          className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm animate-fade-in flex items-center justify-center pointer-events-auto"
          data-ui-element="true"
          onMouseDown={(e) => e.nativeEvent.stopImmediatePropagation()}
          onMouseUp={(e) => e.nativeEvent.stopImmediatePropagation()}
          onMouseMove={(e) => e.nativeEvent.stopImmediatePropagation()}
          onClick={(e) => e.nativeEvent.stopImmediatePropagation()}
          onContextMenu={(e) => e.nativeEvent.stopImmediatePropagation()}
          onWheel={(e) => e.nativeEvent.stopImmediatePropagation()}
        >
           <div className="relative w-full h-full bg-win-bg overflow-hidden shadow-2xl flex flex-col">
              {/* Background Orbs to match WebFileManager */}
              <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-blue-600/20 blur-[120px] rounded-full pointer-events-none" />
              <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-purple-600/20 blur-[120px] rounded-full pointer-events-none" />
              
              <FileManager
                peerId={connectionName || undefined}
                onClose={() => setShowFileManager(false)}
                onOpenNewWindow={() => {
                  window.open(window.location.pathname + '?mode=file-manager', '_blank');
                  setShowFileManager(false);
                }}
              />
           </div>
        </div>
      )}

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} />

      {/* 链路劣化倒计时提示（质量差→倒计时切换，恢复则自动取消） */}
      <DegradeCountdown />

      {/* 文件管理器上传进度 / 冲突确认（全局常驻：剪贴板上传无需打开文件管理器面板） */}
      <UploadManager />
      <ConflictModal />

      {/* 全局文件搜索面板（常驻：工具菜单「文件搜索」可在不打开文件管理器时弹出） */}
      <GlobalSearchPanel />

      {/* 搜索引擎初始化弹窗（常驻：工具菜单触发未初始化时提示初始化） */}
      <SearchEngineInitModal
        isOpen={isSearchEngineInitModalOpen}
        onClose={closeSearchEngineInitModal}
        isReinit={isSearchEngineReinit}
      />

      {/* Crash Notification Modal */}
      {crashInfo && (
        <CrashNotification
          content={crashInfo}
          onDismiss={() => {
            signalingService.sendCrashDelete();
            setCrashInfo(null);
          }}
        />
      )}

      {/* P2P Assist 升级确认弹窗 */}
      <P2PAssistDialog />

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />

      {/* Hidden directory input */}
      <input
        ref={directoryInputRef}
        type="file"
        // @ts-expect-error webkitdirectory is not in standard types
        webkitdirectory=""
        directory=""
        multiple
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />
    </div>
  );
};

export default App;
